import { useEffect, useState } from "react";

import { Api } from "@noom/noomscape";

import { UserProgramInfo } from "@/api/userProgram/getUserProgramInfo";
import { useAppContext } from "@/contexts";
import { Partner } from "@/models";
import { getAutologinData } from "@/utils/cookies";
import { captureException } from "@/utils/sentry";

import { AxiosError } from "axios";

export type MemberPartnerResult = {
  /** The member's partner, or null if they have none (or it is still loading). */
  partner: Partner | null;
  isLoading: boolean;
  /** A failed lookup. A member with no partner is not an error: `partner` is null. */
  error: unknown;
};

const isNotFound = (e: unknown) => (e as AxiosError)?.response?.status === 404;

/**
 * Returns null when the member has no partner (not in a program, no slug, or
 * the partner is gone). Throws on any other failure.
 */
const fetchMemberPartner = async (credentials: {
  accessCode: string;
  accessToken: string;
}): Promise<Partner | null> => {
  try {
    const programInfo: UserProgramInfo = await Api.call(
      "userProgram.getUserProgramInfo",
      Api.api.userProgram.getUserProgramInfo,
      credentials,
    );
    if (!programInfo.partnerSlug) {
      return null;
    }

    return await Api.call(
      "partner.getPartnerBySlug",
      Api.api.partner.getPartnerBySlug,
      { slug: programInfo.partnerSlug },
    );
  } catch (e) {
    if (isNotFound(e)) {
      return null;
    }
    throw e;
  }
};

/**
 * For flows that only have the `autologin_data` cookie (no employer slug or UPID
 * in the URL). Loads the member's partner and saves it in app state so survey
 * events carry the real partner ID and name instead of -1.
 *
 * With no cookie it does nothing: `partner` is null and `isLoading` is false.
 * Callers that require the cookie should check for it themselves.
 */
export const useMemberPartner = (): MemberPartnerResult => {
  const { partnerInfo, setPartnerInfo } = useAppContext();
  const [isLoading, setIsLoading] = useState(!partnerInfo);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    // Already loaded, for example by another hook earlier in the session.
    if (partnerInfo) {
      setIsLoading(false);
      return () => undefined;
    }

    const autologinData = getAutologinData();
    if (!autologinData) {
      setIsLoading(false);
      return () => undefined;
    }

    const { accessCode, accessToken } = autologinData;
    let ignore = false;

    async function load() {
      try {
        const partner = await fetchMemberPartner({ accessCode, accessToken });
        if (ignore) {
          return;
        }
        if (partner) {
          setPartnerInfo(partner);
        }
      } catch (e) {
        if (!ignore) {
          captureException(e);
          setError(e);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      ignore = true;
    };
    // Load once per mount; partnerInfo changing after we set it must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { partner: partnerInfo, isLoading, error };
};
